import { InternalApiListSelectorGroup, InternalApiListSelectorOption } from '../../../api-internal';
import { isAnyLeafSelected } from './SelectorOption';
import { useMemo, useState } from 'react';
import { SelectorOptionMap, SelectorOptionType } from './Selector';
import { ManualCustomListSelectorCode, selectorConfigByType } from './ListGroups';
import { useMe } from '../../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';

interface Props {
  type: ManualCustomListSelectorCode;
  apiSelectorOptions: InternalApiListSelectorOption[];
  firstGroup: InternalApiListSelectorGroup | undefined;
}

export const DJ_REFERENCE_GROUP = 'DJREFERENCEGROUP';

export const useSelectorOptions = ({ type, apiSelectorOptions, firstGroup }: Props) => {
  const { features } = useMe();
  const areReferenceGroupsHidden = !isFeatureEnabled(ORGANISATION_FEATURE.LIST_GROUPS_WITH_REFERENCE_GROUPS, features);

  const initialSelectorOptionMap = useMemo(
    () => getSelectorOptions(type, apiSelectorOptions, areReferenceGroupsHidden),
    [type, apiSelectorOptions]
  );

  const [selectorOptionMap, setSelectorOptionMap] = useState<SelectorOptionMap>(() => {
    return getSelectorOptionsWithGroup(initialSelectorOptionMap, firstGroup);
  });

  return {
    selectorOptionMap,
    setSelectorOptionMap,
    applyGroupToSelectorOptions: (group: InternalApiListSelectorGroup | undefined) => {
      const newSelectorOptionMap = getSelectorOptionsWithGroup(selectorOptionMap, group);
      setSelectorOptionMap(newSelectorOptionMap);
      return newSelectorOptionMap;
    },
  };
};

const getSelectorOptionsWithGroup = (selectorOptionMap: SelectorOptionMap, group?: InternalApiListSelectorGroup) => {
  selectorOptionMap.forEach((option) => {
    option.isSelected = false;
    option.isSubscribed = false;
  });
  group?.enabledSelectorOptions.forEach((option) => {
    selectorOptionMap.get(option)!.isSelected = true;
  });
  group?.subscribedSelectorOptions.forEach((option) => {
    selectorOptionMap.get(option)!.isSubscribed = true;
  });

  selectorOptionMap.forEach((option) => {
    option.children.sort(
      (a, b) => Number(isAnyLeafSelected(b) || b.isSubscribed) - Number(isAnyLeafSelected(a) || a.isSubscribed)
    );
  });

  return new Map(selectorOptionMap);
};

// TODO wrap in promise and show loading screen
const getSelectorOptions = (
  type: ManualCustomListSelectorCode,
  apiSelectorOptions: InternalApiListSelectorOption[],
  areReferenceGroupsHidden: boolean
): SelectorOptionMap => {
  // speeds up changing the selected group
  const selectorOptionMap = new Map<string, SelectorOptionType>();

  const startTime = new Date();

  const { rootOptionValue } = selectorConfigByType[type];
  const apiRootOptions = apiSelectorOptions.filter((option) =>
    rootOptionValue !== 'DEFAULT_ROOT' ? option.parentValue.includes(rootOptionValue) : !option.parentValue.length
  );

  let rootOptions = apiRootOptions
    .map((option) => {
      const [title, ...descriptions] = option.descriptions;
      return {
        title,
        descriptions,
        value: option.value,
        children: getSelectorOptionChildren(option, apiSelectorOptions, selectorOptionMap, areReferenceGroupsHidden),
      };
    })
    .map((option) => {
      selectorOptionMap.set(option.value, option);

      return option;
    })
    .toSorted((a, b) => a.title.localeCompare(b.title));

  if (type === ManualCustomListSelectorCode.DJ_SANCTION_LISTS) {
    let index = rootOptions.findIndex((option) => option.value === 'PERSON.UNITEDSTATES');
    rootOptions = rootOptions.toSpliced(index, 1).toSpliced(0, 0, rootOptions[index]);
    index = rootOptions.findIndex((option) => option.value === 'PERSON.UNITEDKINGDOM');
    rootOptions = rootOptions.toSpliced(index, 1).toSpliced(0, 0, rootOptions[index]);
    index = rootOptions.findIndex((option) => option.value === 'PERSON.INTERNATIONAL');
    rootOptions = rootOptions.toSpliced(index, 1).toSpliced(0, 0, rootOptions[index]);
    index = rootOptions.findIndex((option) => option.value === 'PERSON.EUROPEANUNION');
    rootOptions = rootOptions.toSpliced(index, 1).toSpliced(0, 0, rootOptions[index]);
  }

  selectorOptionMap.set(rootOptionValue, { title: '', value: '', descriptions: [], children: rootOptions });

  // eslint-disable-next-line no-console
  console.log('Calculating Selectors Took time:', new Date().getTime() - startTime.getTime());
  return selectorOptionMap;
};

export const getSelectorOptionChildren = (
  apiParentSelectorOption: InternalApiListSelectorOption,
  apiSelectorOptions: InternalApiListSelectorOption[],
  selectorOptionMap: SelectorOptionMap,
  areReferenceGroupsHidden: boolean
): SelectorOptionType[] => {
  const children = apiSelectorOptions.filter((child) => child.parentValue.includes(apiParentSelectorOption.value));
  return children
    .filter((child) => {
      return areReferenceGroupsHidden ? !child.value.includes(DJ_REFERENCE_GROUP) : true;
    })
    .map((child) => {
      const [title, ...descriptions] = child.descriptions;
      return (
        // use same reference for repeating nodes
        selectorOptionMap.get(child.value) ?? {
          title,
          descriptions,
          value: child.value,
          children: getSelectorOptionChildren(child, apiSelectorOptions, selectorOptionMap, areReferenceGroupsHidden),
        }
      );
    })
    .map((option) => {
      selectorOptionMap.set(option.value, option);
      return option;
    })
    .toSorted((a, b) => a.title.localeCompare(b.title));
};
